import axios from 'axios';
import { getValidUserSession, clearUserSession, isTokenExpired } from '../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function isPro(user) {
  return Boolean(user && (user.plan === 'pro' || user.plan === 'enterprise'));
}

/**
 * Who is using the template right now. The stored session decides, not the
 * caller's copy of the user: a cached user whose token has expired is signed
 * out. A designer-portal sign-in (its own keys) is accepted by the same
 * endpoints, so it counts too.
 */
function currentSession() {
  const site = getValidUserSession();
  if (site) return site;
  const designerToken = localStorage.getItem('user_token');
  if (designerToken && !isTokenExpired(designerToken)) {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      if (user) return { user, token: designerToken };
    } catch { /* unreadable — treat as signed out */ }
  }
  return null;
}

// Template ids with a request still running: a double click must not create
// two copies of the same template.
const inFlight = new Set();

export async function useTemplate(template, deps) {
  const {
    navigate, openAuthModal, openUpgradeModal, toast,
  } = deps;

  // After signing in, run again with the session the sign-in just stored.
  // `deps` was built before the sign-in, so its `user` is the old one.
  const askToSignIn = () => openAuthModal({
    onSuccess: (authedUser) => useTemplate(template, { ...deps, user: authedUser || null }),
  });

  const session = currentSession();
  if (!session) {
    askToSignIn();
    return;
  }
  const user = deps.user && deps.user.id === session.user.id ? deps.user : session.user;
  const auth = { headers: { Authorization: `Bearer ${session.token}` } };

  if (inFlight.has(template.id)) return;
  inFlight.add(template.id);
  try {
    if (isPro(user)) {
      try {
        const { data } = await axios.post(
          `${API_URL}/api/templates/${template.id}/instantiate`,
          undefined,
          auth,
        );
        navigate(`/editor/${data.design_id}`);
      } catch (err) {
        const status = err?.response?.status;
        const code = err?.response?.data?.detail?.code;
        if (status === 401) {
          // The server no longer accepts this session (expired or revoked).
          clearUserSession();
          askToSignIn();
        } else if (status === 403 && code === 'upgrade_required') {
          openUpgradeModal && openUpgradeModal({ feature: 'save' });
        } else if (status === 403 && code === 'use_template_disabled') {
          toast && toast({ title: 'The template owner has disabled new uses.' });
        } else if (status === 404) {
          toast && toast({ title: 'This template is no longer available.' });
        } else {
          toast && toast({ title: 'Unable to use this template right now.' });
        }
      }
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/templates/${template.id}/track`,
        { event_type: 'template_use' },
        auth,
      );
    } catch {
      // Analytics failure must not block navigation.
    }
    navigate(`/editor?template_id=${template.id}&session=shared`);
  } finally {
    inFlight.delete(template.id);
  }
}
